import type { ReactNode } from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

/**
 * Side options for tooltip positioning.
 */
export type SidebarTooltipSide = "right" | "left" | "top" | "bottom";

interface SidebarTooltipProps {
  /** Tooltip content — usually a label, occasionally a short two-line card. */
  content: ReactNode;
  /** Element to wrap with tooltip */
  children: ReactNode;
  /** When true, renders children without tooltip wrapper. Defaults to false. */
  disabled?: boolean;
  /** Side where tooltip appears. Defaults to 'right' (sidebar is on left). */
  side?: SidebarTooltipSide;
}

/**
 * SidebarTooltip - Atom component
 *
 * Wraps content with a tooltip, typically shown when the sidebar is collapsed.
 * Uses shadcn/ui Tooltip components. When disabled, renders children directly
 * without any tooltip wrapper.
 *
 * Part of the atomic design system for NavigationSidebar.
 *
 * @example
 * ```tsx
 * // Basic usage - tooltip on right (default)
 * <SidebarTooltip content="Dashboard">
 *   <SidebarIcon icon={Home} />
 * </SidebarTooltip>
 *
 * // Disabled when sidebar is expanded
 * <SidebarTooltip content="Dashboard" disabled={!isCollapsed}>
 *   <SidebarIcon icon={Home} />
 * </SidebarTooltip>
 *
 * // Custom side positioning
 * <SidebarTooltip content="Settings" side="bottom">
 *   <SidebarIcon icon={Settings} />
 * </SidebarTooltip>
 * ```
 */
export function SidebarTooltip({
  content,
  children,
  disabled = false,
  side = "right",
}: SidebarTooltipProps) {
  // When disabled, render children directly without tooltip wrapper
  if (disabled) {
    return <>{children}</>;
  }

  return (
    <Tooltip disableHoverableContent>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side={side}>{content}</TooltipContent>
    </Tooltip>
  );
}
